import { useCallback, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";

interface WorkspaceLike {
  id: string;
}
interface QueryStatus {
  type: string;
}

interface UseFirstRunGateArgs {
  /** The signed-in user id, or "" before auth resolves. */
  userId: string;
  /** Account email (WorkOS) — seeds the workspace bootstrap. */
  userEmail: string;
  /** First name, for prefilling the workspace name + profile seed. */
  firstName?: string;
  /** Current scope's workspace (null on Home). */
  selectedWorkspace: string | null;
  workspaceList: ReadonlyArray<WorkspaceLike>;
  workspaceListStatus: QueryStatus;
  /** The app's shared 15s boot budget — bounds the cover so it can't stick. */
  bootstrapTimedOut: boolean;
}

/** Seeds handed to `FirstRunWizard` when a brand-new user is onboarding. */
export interface FirstRunSeeds {
  userId: string;
  userEmail: string;
  creatorHomeRegion: "us" | "eu";
  initialName: string;
  initialAvatarUrl: string | null;
  initialHeadline: string;
  initialWorkspaceName: string;
}

/**
 * The boot decision for a signed-in user:
 *
 * - `cover` — we don't yet know the destination (workspace list still
 *   resolving, or a persisted scope we can't validate). Hold the boot cover —
 *   never paint a shell we'd have to immediately replace.
 * - `onboarding` — a genuinely new user (0 workspaces, server-confirmed). Render
 *   the wizard full-screen INSTEAD of the app shell.
 * - `app` — a returning user. Render the normal app shell.
 */
export type FirstRunGate =
  | { kind: "cover" }
  | { kind: "onboarding"; seeds: FirstRunSeeds }
  | { kind: "app" };

export interface UseFirstRunGateResult {
  gate: FirstRunGate;
  /** Pin the wizard mounted through the Finish writes (the new workspace
   *  syncing into the list would otherwise flip the gate to `app` mid-commit). */
  onFinishStart: () => void;
  /** Un-pin if Finish fails (e.g. slug collision). */
  onFinishEnd: () => void;
  /** Close the flow for good once the workspace is created. */
  onDismiss: () => void;
}

interface UserRowLike {
  email?: string;
  display_name?: string;
  avatar_url?: string | null;
  headline?: string | null;
  home_region?: string;
  /** Agent/coworker accounts are never put through human onboarding. */
  is_coworker?: boolean;
}

/**
 * Single owner of the post-auth boot decision (see the "boot flicker" rationale
 * in `components/app/CLAUDE.md`). Lifting this OUT of the old `FirstRunFlow`
 * overlay and UP to `AppInner` is what lets the boot cover stay continuous from
 * sign-in → wizard and stops the app shell painting (workspace shell, empty
 * Home) for a user who's about to be onboarded.
 *
 * Offline-safe: a returning user has workspace rows in the local Zero replica
 * essentially immediately, so we return `app` the moment the list is non-empty —
 * without waiting for the server-confirmed `complete` status (which never
 * arrives offline). The cover is only held for an unresolved list / unvalidated
 * scope, and is bounded by the shared `bootstrapTimedOut` budget so a stalled or
 * offline query can never lock the app behind the cover.
 */
export function useFirstRunGate({
  userId,
  userEmail,
  firstName,
  selectedWorkspace,
  workspaceList,
  workspaceListStatus,
  bootstrapTimedOut,
}: UseFirstRunGateArgs): UseFirstRunGateResult {
  const [userRow, userRowStatus] = useQuery(
    userId ? queries.users.byId(userId) : null,
  );
  const [dismissed, setDismissed] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const onFinishStart = useCallback(() => setFinishing(true), []);
  const onFinishEnd = useCallback(() => setFinishing(false), []);
  const onDismiss = useCallback(() => setDismissed(true), []);

  const gate = computeFirstRunGate({
    userId,
    userEmail,
    firstName,
    selectedWorkspace,
    workspaceList,
    workspaceListStatus,
    bootstrapTimedOut,
    userRow: userRow as UserRowLike | undefined,
    userRowComplete: userRowStatus.type === "complete",
    dismissed,
    finishing,
  });

  return { gate, onFinishStart, onFinishEnd, onDismiss };
}

interface ComputeArgs extends UseFirstRunGateArgs {
  userRow: UserRowLike | undefined;
  userRowComplete: boolean;
  dismissed: boolean;
  finishing: boolean;
}

/**
 * Pure boot-gate decision — exported for unit tests (no React, no timers). The
 * order of the branches is the contract; see `FirstRunGate` for what each means.
 */
export function computeFirstRunGate(args: ComputeArgs): FirstRunGate {
  const {
    userId,
    userEmail,
    firstName,
    selectedWorkspace,
    workspaceList,
    workspaceListStatus,
    bootstrapTimedOut,
    userRow,
    userRowComplete,
    dismissed,
    finishing,
  } = args;

  if (dismissed || !userId) return { kind: "app" };

  // Agents/coworkers are never put through the human onboarding wizard — they
  // connect to EXISTING workspaces via the CLI/API, they don't create one. A
  // coworker account that signs into the desktop app (e.g. the e2e bot user) has
  // no personal workspace but must still land in the app, not onboarding. Once
  // the row resolves, `is_coworker` short-circuits to the shell; until then the
  // branches below hold the cover, so a coworker never flashes the wizard.
  if (userRow?.is_coworker) return { kind: "app" };

  const count = workspaceList.length;
  const listComplete = workspaceListStatus.type === "complete";

  // Returning user — local replica already has a workspace. Don't wait for the
  // server-confirmed `complete` (works offline). But if the active scope points
  // at a workspace we can't see yet (stale persisted `ano-scope`), hold the
  // cover rather than flash a broken "Loading…" workspace shell — auto-recovery
  // corrects the scope once the list completes.
  if (count > 0 && !finishing) {
    const scopeUnsettled =
      selectedWorkspace !== null &&
      !listComplete &&
      !workspaceList.some((w) => w.id === selectedWorkspace);
    if (scopeUnsettled && !bootstrapTimedOut) return { kind: "cover" };
    return { kind: "app" };
  }

  const newUserConfirmed = listComplete && count === 0;
  if (newUserConfirmed || finishing) {
    // Hold briefly for the user row so the wizard seeds correctly; give up at
    // the boot timeout (fall through to the firstName fallback) so we can't stick.
    if (!userRowComplete && !finishing && !bootstrapTimedOut) {
      return { kind: "cover" };
    }
    return {
      kind: "onboarding",
      seeds: {
        userId,
        userEmail: userEmail || userRow?.email || "",
        creatorHomeRegion: userRow?.home_region === "eu" ? "eu" : "us",
        initialName: userRow?.display_name ?? firstName ?? "",
        initialAvatarUrl: userRow?.avatar_url ?? null,
        initialHeadline: userRow?.headline ?? "",
        initialWorkspaceName: firstName
          ? `${firstName}'s Workspace`
          : "My Workspace",
      },
    };
  }

  // Empty list, not yet server-confirmed. Hold the cover while plausibly still
  // loading (no Home flash for a new user), bounded so offline can't lock the app.
  if (!bootstrapTimedOut) return { kind: "cover" };
  return { kind: "app" };
}
